import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ShopOffer, ShopOfferType } from '@shared/models/offer.model';
import { ShopOffersProvider, ShopOffersCreatePurchaseResponse, ShopOffersRate } from '@shared/models/shop-offers.model';
import { EventManager } from '@app/shared/utils/event-manager';
import { isModalConfig, Modal, ModalConfig, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { ShopService } from '@app/shared/services/shop.service';
import { TakeUntilDestroy } from '@app/shared/decorators/take-until-destroy.decorator';
import { NativeService } from '@shared/services/native.service';
import { Observable } from 'rxjs';
import { TokenService } from '@shared/services/token.service';
import { AnimatedTimerComponent } from '@app/shared/components/animated-timer/animated-timer.component';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { FormatShopPrice } from '../../pipes/format-shop-price.pipe';
import {
  WizardModalComponent,
  WizardModalData,
  WizardModalSteps,
} from '@app/shared/components/modals/wizard-modal/wizard-modal.component';
import { ParamModalEnum } from '@app/shared/models/queryParam.model';
import { SharedModule } from '@app/shared/shared.module';
import { AnalyticsService } from '@app/shared/services/analytics.service';
import { OffersRepository } from '@app/shared/state/offer/offer.repository';
import { UserRepository } from '@app/shared/state/user/user.repository';
import { DisplayComponent } from '@app/shared/components/display/display.component';
import { EventsRepository } from '@app/shared/state/events/events.repository';
import { LimitedTimeEventType } from '@app/shared/models/limitedTimeEvent.model';
import { RecurringRepository } from '@app/shared/state/recurring/recurring.repository';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-offer-modal',
  templateUrl: './offer.component.html',
  styleUrls: ['./offer.component.scss'],
  standalone: true,
  imports: [
    AnimatedTimerComponent,
    ButtonComponent,
    FormatShopPrice,
    WizardModalComponent,
    DisplayComponent,
    SharedModule,
  ],
})
export class OfferModalComponent implements OnInit {
  offers = signal<ShopOffer[]>([]);
  offersType: ShopOfferType[] = [];
  freeCoinsPercentage: number[] = [];
  wizardModalData: WizardModalData;

  tab = signal(0);

  componentDestroy: () => Observable<unknown>;

  private readonly shopService = inject(ShopService);
  private readonly userRepo = inject(UserRepository);
  private readonly offersRepo = inject(OffersRepository);
  private readonly eventManager = inject(EventManager);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly nativeService = inject(NativeService);
  private readonly tokenService = inject(TokenService);
  private readonly dialog = inject(MatDialog);
  private readonly eventsRepo = inject(EventsRepository);
  private readonly recurringRepo = inject(RecurringRepository);

  private readonly offerModalData = inject(MAT_DIALOG_DATA) as Modal | ModalConfig;

  private readonly visibleEvents = toSignal(this.eventsRepo.visibleLimitedTimeEvents$, { initialValue: [] });

  valueMultiplied = 0;

  multiplierPrize = computed(
    () => this.visibleEvents().find((e) => e.type === LimitedTimeEventType.MultiplierPrize) ?? null
  );

  recurringValue = toSignal(this.recurringRepo.recurringBonus$, { initialValue: 0 });

  magicbag = computed(() => {
    const currentOffer = this.offers()[this.tab()];
    if (!currentOffer) return null;

    return (
      this.visibleEvents().find(
        (e) =>
          e.type === LimitedTimeEventType.Magicbag &&
          Number(currentOffer.finalPrice.amount) >= Number(e.properties?.minlocalprice)
      ) ?? null
    );
  });

  ngOnInit() {
    let notFunds = false;
    let targetSku: string | null = null;

    if (!isModalConfig(this.offerModalData)) {
      notFunds = this.offerModalData?.data?.notFunds || false;
      targetSku = this.offerModalData?.data?.targetSku || null;
    } else {
      notFunds = this.offerModalData?.properties?.notFunds || false;
      targetSku = this.offerModalData?.properties?.targetSku || null;
    }

    if (notFunds) {
      const notFundsOffer = this.offersRepo.getNotFunds()[0];

      this.offers.set([notFundsOffer]);
      this.getShopOfferType(notFundsOffer, true);
    } else {
      this.offersRepo.offers$
        .pipe(
          distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
          takeUntil(this.componentDestroy())
        )
        .subscribe((offers) => {
          this.tabChanged(0);

          if (!offers.length) {
            this.closeModal();
            return;
          }

          this.offers.set(offers);
          this.offersType = offers.map((o) => this.resolveOfferType(o));
          this.freeCoinsPercentage = offers.map((o) => this.calcFreeCoinsPercentage(o));
          offers.forEach((o) => this.analyticsService.logEvent('custom_iapPopupOffer_impression', o.sku));

          if (this.offers().length > 1) {
            const initialTab = targetSku
              ? Math.max(
                  0,
                  this.offers().findIndex((o) => o.sku === targetSku)
                )
              : 0;
            this.setupWizardOffer(initialTab);
            this.tabChanged(initialTab);
          }
        });
    }
  }

  purchaseOffer(offer: ShopOffer): void {
    this.shopService
      .createPurchaseShopOffers(
        this.userRepo.getUser(),
        new ShopOffersRate(offer.rateId, offer.priceSku, offer.value, offer.tag),
        offer.currency,
        this.getShopOffersProvider(),
        this.tokenService.getCurrentInfo().shopLevel,
        offer.offerId
      )
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe((purchaseResponse: ShopOffersCreatePurchaseResponse) => {
        this.closeModal();

        this.shopService.notifyCreatePurchaseShopOffers(
          this.getShopOffersProvider(),
          purchaseResponse,
          offer.finalPrice.isoCurrency,
          offer.offerId
        );
      });
  }

  closeModal(): void {
    const modalType = isModalConfig(this.offerModalData) ? ModalType.Offer : ModalType.View;

    if (this.dialog.getDialogById(NonPersistedModalName.OfferModalComponent)) {
      this.eventManager.closeModal(
        new Modal(NonPersistedModalName.OfferModalComponent, modalType, this.offerModalData)
      );
    }
  }

  tabChanged(tab: number): void {
    this.tab.set(tab);
  }

  private setupWizardOffer(initialTab: number = 0): void {
    const offersWizard: WizardModalSteps[] = [];
    const modalType = isModalConfig(this.offerModalData) ? ModalType.Offer : ModalType.View;

    this.offers().forEach((offer, i) => {
      offersWizard[i] = {
        ...offer,
        closeable: true,
        imageUrl: offer.creativity,
        buttonText: offer.claim,
      };

      this.getShopOfferType(offer);
    });

    this.wizardModalData = new WizardModalData(
      NonPersistedModalName.OfferModalComponent,
      modalType,
      offersWizard,
      false,
      String(ParamModalEnum.Shop),
      initialTab
    );
  }

  private getShopOffersProvider(): ShopOffersProvider {
    return this.nativeService.getShopOffersProvider();
  }

  private resolveOfferType(offer: ShopOffer, notFunds: boolean = false): ShopOfferType {
    const prefix = notFunds ? 'NotFunds' : '';

    if (offer.freeCoins && offer.discountPercent) {
      return `${prefix}FreeCoinsDiscountPercent`;
    }
    if (offer.freeCoins) return `${prefix}FreeCoins`;
    if (offer.discountPercent) return `${prefix}DiscountPercent`;

    return notFunds ? 'NotFunds' : null;
  }

  private calcFreeCoinsPercentage(offer: ShopOffer): number {
    return Math.round((offer.freeCoins * 100) / offer.value);
  }

  private getShopOfferType(offer: ShopOffer, notFunds: boolean = false): void {
    this.analyticsService.logEvent('custom_iapPopupOffer_impression', offer.sku);

    this.getFreeCoinsPercentage(offer);

    this.offersType.push(this.resolveOfferType(offer, notFunds));
  }

  private getFreeCoinsPercentage(offer: ShopOffer): void {
    this.freeCoinsPercentage.push(Math.round((offer.freeCoins * 100) / offer.value));
  }
}
